import React, { useEffect, useState } from 'react';
import WorkOrders from '../models/WorkOrders';

import {Button} from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { useFilterContext } from '../context/SharedFilterContext';

import DataTable from 'react-data-table-component';
import { ImSpinner3 } from 'react-icons/im';

import {
  BrowserRouter as Router,
  useLocation, 
  useHistory
} from "react-router-dom";

import {MdArrowBack} from 'react-icons/md';
import moment from 'moment';
import pLimit from 'p-limit';   // tiny 0-dep helper (npm i p-limit)
import { FaCalendarAlt, FaFilter, FaFileExport, FaSync, FaSave, FaExternalLinkAlt } from 'react-icons/fa';


const WorkOrdersScreen = () => {
  const [corrigoWorkOrders, setCorrigoWorkOrders] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0); // Track the current offset
  const [hasMore, setHasMore] = useState(true); // Track if more work orders are available
  const [exportLoading, setExportLoading] = useState(false);
  const { user } = useAuth();

  // Date range + filters come from the shared TopNavBar filter context so this
  // screen stays in sync with the Inspections page (and Mike's context).
  const {
    startDate,
    endDate,
    inspectionViewMode: viewMode,
    selectedPropertyList,
    favoritedPropertyIds
  } = useFilterContext();

  const [allWorkOrders, setAllWorkOrders] = useState([]); // Store all work orders
  const [filteredWorkOrders, setFilteredWorkOrders] = useState([]); // Store filtered work orders

  // Derived from the shared favorites cache (populated by TopNavBar)
  const hasFavorites = (favoritedPropertyIds?.length || 0) > 0;

  const history = useHistory();

const goBack = () => {
  history.push(`/migration`);
}

  const CHUNK_SIZE = 100;
  const MAX_CONCURRENCY = 6; // start with 4-8; tune to taste

  const limit = pLimit(MAX_CONCURRENCY);

  const fetchChunk = async (ids) => {
    const searchList = [{ field: 'work_order_id', value: ids, comparison: 'IN' }];
    const { data } = await WorkOrders.filterBy(
      searchList, 'dt_created', 'desc', 10000, undefined, undefined, undefined, true
    );
    return data;
  };

  // Add function to filter work orders based on view mode
  const applyWorkOrderFiltering = async (workOrders, mode, explicitList = null) => {
    console.log(`\n========== FILTERING WORK ORDERS - MODE: ${mode} ==========`);
    console.log(`Total work orders before filtering: ${workOrders.length}`);

    // Log sample of work order property_uuid values for debugging
    const sampleWorkOrders = workOrders.slice(0, 5);
    console.log("Sample work orders with property_uuid:");
    sampleWorkOrders.forEach((wo, index) => {
      console.log(`  WO ${index + 1}: work_order_id=${wo.work_order_id}, property_uuid=${wo.property_uuid}, property_number=${wo.property_number}`);
    });

    // Log unique property_uuid values in work orders
    const uniquePropertyUuids = [...new Set(workOrders.filter(wo => wo.property_uuid).map(wo => wo.property_uuid))];
    console.log(`Unique property_uuid values in work orders: ${uniquePropertyUuids.length}`);
    if (uniquePropertyUuids.length <= 20) {
      console.log("All unique property_uuid values:", uniquePropertyUuids);
    } else {
      console.log("First 20 unique property_uuid values:", uniquePropertyUuids.slice(0, 20));
    }

    if (mode === 'favorited') {
      console.log("\n--- FAVORITED MODE ---");
      const favoriteUuids = favoritedPropertyIds || [];

      console.log(`Number of favorited property UUIDs: ${favoriteUuids.length}`);
      console.log("Favorited property UUIDs:", favoriteUuids);

      if (favoriteUuids.length === 0) {
        console.log("No favorites found, returning empty array");
        setFilteredWorkOrders([]);
        return;
      }

      // Filter work orders where property_uuid is in user's favorites
      const favoritedWorkOrders = workOrders.filter(workOrder => {
        const hasPropertyUuid = !!workOrder.property_uuid;
        const isInFavorites = hasPropertyUuid && favoriteUuids.includes(workOrder.property_uuid);

        // Log details for first few work orders for debugging
        if (workOrders.indexOf(workOrder) < 3) {
          console.log(`  Checking WO ${workOrder.work_order_id}:`, {
            property_uuid: workOrder.property_uuid,
            hasPropertyUuid,
            isInFavorites
          });
        }

        return isInFavorites;
      });

      console.log(`\nFiltered ${favoritedWorkOrders.length} work orders for favorited properties`);
      console.log("First 5 filtered work orders:", favoritedWorkOrders.slice(0, 5).map(wo => ({
        work_order_id: wo.work_order_id,
        property_uuid: wo.property_uuid
      })));

      setFilteredWorkOrders(favoritedWorkOrders);

    } else if (mode === 'propertyList') {
      console.log("\n--- PROPERTY LIST MODE ---");

      // Use the explicitly passed list if available, otherwise use the one from state
      const listToUse = explicitList || selectedPropertyList;

      if (!listToUse) {
        console.log("No property list selected, returning empty array");
        setFilteredWorkOrders([]);
        return;
      }

      console.log(`Selected property list: ${listToUse.name} (UUID: ${listToUse.uuid})`);

      // Get property UUIDs from the selected list
      let propertyUuids = [];
      if (listToUse.properties_list) {
        try {
          propertyUuids = JSON.parse(listToUse.properties_list);
          console.log(`Successfully parsed property list with ${propertyUuids.length} properties`);
          console.log("Property UUIDs in list:", propertyUuids);
        } catch (e) {
          console.error('Error parsing properties_list:', e);
          propertyUuids = [];
        }
      } else {
        console.log("No properties_list field in selected property list");
      }

      if (propertyUuids.length === 0) {
        console.log("Property list is empty, returning empty array");
        setFilteredWorkOrders([]);
        return;
      }

      // Filter work orders where property_uuid is in the property list
      const propertyListWorkOrders = workOrders.filter(workOrder => {
        const hasPropertyUuid = !!workOrder.property_uuid;
        const isInPropertyList = hasPropertyUuid && propertyUuids.includes(workOrder.property_uuid);

        // Log details for first few work orders for debugging
        if (workOrders.indexOf(workOrder) < 3) {
          console.log(`  Checking WO ${workOrder.work_order_id}:`, {
            property_uuid: workOrder.property_uuid,
            hasPropertyUuid,
            isInPropertyList
          });
        }

        return isInPropertyList;
      });

      console.log(`\nFiltered ${propertyListWorkOrders.length} work orders for property list "${listToUse.name}"`);
      console.log("First 5 filtered work orders:", propertyListWorkOrders.slice(0, 5).map(wo => ({
        work_order_id: wo.work_order_id,
        property_uuid: wo.property_uuid
      })));

      // Additional debugging: Check if any property list UUIDs match work order UUIDs
      const matchingUuids = propertyUuids.filter(uuid =>
        workOrders.some(wo => wo.property_uuid === uuid)
      );
      console.log(`Property UUIDs from list that have work orders: ${matchingUuids.length}`);
      if (matchingUuids.length > 0 && matchingUuids.length <= 10) {
        console.log("Matching UUIDs:", matchingUuids);
      }

      const nonMatchingUuids = propertyUuids.filter(uuid =>
        !workOrders.some(wo => wo.property_uuid === uuid)
      );
      console.log(`Property UUIDs from list with NO work orders: ${nonMatchingUuids.length}`);
      if (nonMatchingUuids.length > 0 && nonMatchingUuids.length <= 10) {
        console.log("Non-matching UUIDs:", nonMatchingUuids);
      }

      setFilteredWorkOrders(propertyListWorkOrders);

    } else {
      console.log("\n--- ALL MODE ---");
      console.log(`Showing all ${workOrders.length} work orders`);
      setFilteredWorkOrders(workOrders);
    }

    console.log(`========== END FILTERING ==========\n`);
  };

  const fetchFilteredWorkOrders = async () => {
    console.log("[WorkOrdersScreen] Fetching filtered work orders");
    setLoading(true);

    const startTime = Date.now(); // Start timing

    try {
      // Fetch work order IDs by date range
      const workOrderIds = await WorkOrders.getWorkOrderIdsByDateRange(
        moment(startDate).format('YYYY-MM-DD HH:mm:ss'),
        moment(endDate).add(1, 'day').format('YYYY-MM-DD HH:mm:ss')
      );

      console.log("Work Order IDs by Date Range:", workOrderIds);

      // Break work order IDs into chunks of 100
      const chunks = [];
      for (let i = 0; i < workOrderIds.length; i += CHUNK_SIZE) {
        chunks.push(workOrderIds.slice(i, i + CHUNK_SIZE));
      }

      // Fetch work orders for each chunk in parallel, limited by p-limit
      let allWorkOrders = [];
      await Promise.all(chunks.map(chunk => 
        limit(() => fetchChunk(chunk).then(res => allWorkOrders.push(...res)))
      ));

      // Filter out work orders with source 'workzone_area_match_historical_context'
      // allWorkOrders = allWorkOrders.filter(order => order.source !== 'workzone_area_match_historical_context');

      // Store all work orders
      setAllWorkOrders(allWorkOrders);
      console.log("allWorkOrders after filtering", allWorkOrders);

      // Filtering is applied by the shared-filter effect once allWorkOrders updates

      setHasMore(false); // No more data to load since we're fetching all at once
    } catch (err) {
      console.error("[WorkOrdersScreen] Error fetching work orders:", err);
      setError(err);
    } finally {
      setLoading(false);
      const endTime = Date.now(); // End timing
      const durationInSeconds = (endTime - startTime) / 1000;
      console.log(`[WorkOrdersScreen] Time taken to fetch all work orders: ${durationInSeconds} seconds`);
    }
  };

  useEffect(() => {
    // Fetch work orders whenever the shared date range changes
    fetchFilteredWorkOrders();
  }, [startDate, endDate]);

  // Re-apply view-mode / property-list / favorites filtering when the shared
  // TopNavBar filters change — no network refetch, just re-filter the already
  // fetched set so this table mirrors the Inspections work orders section.
  useEffect(() => {
    applyWorkOrderFiltering(allWorkOrders, viewMode);
  }, [viewMode, selectedPropertyList, favoritedPropertyIds, allWorkOrders]);

  // Update corrigoWorkOrders when filteredWorkOrders changes
  useEffect(() => {
    setCorrigoWorkOrders(filteredWorkOrders);
  }, [filteredWorkOrders]);

  // Add new state for tracking updates
  const [updatingAvoid, setUpdatingAvoid] = useState(new Set());

  // Add function to handle avoid button click
  const handleAvoidClick = async (workOrderId, currentAvoidValue) => {
    console.log(`[WorkOrdersScreen] Updating avoid for work order ${workOrderId} from ${currentAvoidValue} to 0`);
    
    // Add to updating set to show loading state
    setUpdatingAvoid(prev => new Set(prev).add(workOrderId));
    
    try {
      await WorkOrders.updateWorkOrderAvoid(workOrderId, 0);
      
      // Update local state to reflect the change
      setCorrigoWorkOrders(prev => 
        prev.map(order => 
          order.work_order_id === workOrderId 
            ? { ...order, avoid: 0 }
            : order
        )
      );
      
      console.log(`[WorkOrdersScreen] Successfully updated avoid for work order ${workOrderId}`);
    } catch (error) {
      console.error(`[WorkOrdersScreen] Error updating avoid for work order ${workOrderId}:`, error);
      // You might want to show a toast notification here
    } finally {
      // Remove from updating set
      setUpdatingAvoid(prev => {
        const newSet = new Set(prev);
        newSet.delete(workOrderId);
        return newSet;
      });
    }
  };

  // Helper function to safely parse dates for sorting
  const sortDate = (rowA, rowB, selector) => {
    const dateA = rowA[selector] ? new Date(rowA[selector]) : null;
    const dateB = rowB[selector] ? new Date(rowB[selector]) : null;
    
    // Handle null/invalid dates
    if (!dateA && !dateB) return 0;
    if (!dateA) return -1;
    if (!dateB) return 1;
    
    // Compare valid dates
    return dateA.getTime() - dateB.getTime();
  };

  const allColumns = [
    {
      name: 'Corrigo Link',
      width: '100px',
      id: 'corrigo_link',
      cell: row => (
        <a
          href={`https://enterprise.corrigo.com/corpnet/workorder/workorderdetails.aspx/${row.work_order_id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: '#0066cc',
            textDecoration: 'none',
            fontSize: '14px',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f7ff'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          title={`View Work Order ${row.work_order_id} in Corrigo Enterprise`}
        >
          View <FaExternalLinkAlt size={12} />
        </a>
      )
    },
    { 
        name: 'DT Created', 
        selector: row => row.dt_created, 
        sortable: true, 
        width: '170px', 
        wrap: true,
        sortFunction: (rowA, rowB) => sortDate(rowA, rowB, 'dt_created'),
        id: 'dt_created'
      },
      // { name: 'Root_Comp', selector: row => row.root_comp, sortable: true, width: '150px', wrap: true, id: 'number' },
      {
        name: 'Root_Comp',
        selector: row => row.root_comp,
        sortable: true,
        width: '200px',
        id: 'root_comp',
        cell: row => (
          <div
            title={row.root_comp}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {row.root_comp}
          </div>
        )
      },

    { name: 'Number', selector: row => row.number, sortable: true, width: '150px', wrap: true, id: 'number' },

    { name: 'Work Order ID', selector: row => row.work_order_id, sortable: true, width: '150px', wrap: true, id: 'work_order_id' },
    { name: 'Corrigo Table ID', selector: row => row.id, sortable: true, width: '145px', wrap: true, id: 'id' },
    { name: 'Corrigo Table UUID', selector: row => row.uuid, sortable: true, width: '200px', wrap: true, id: 'uuid' },
    { name: 'Main Asset ID', selector: row => row.main_asset_id, sortable: true, width: '150px', wrap: true, id: 'main_asset_id' },
    { name: 'Property Number', selector: row => row.property_number, sortable: true, width: '150px', wrap: true, id: 'property_number' },
    // { name: 'Task Refinement', selector: row => row.task_refinement, sortable: true, width: '250px', wrap: true, id: 'task_refinement' },
    {
      name: 'Task Refinement',
      selector: row => row.task_refinement,
      sortable: true,
      width: '350px',
      id: 'task_refinement',
      cell: row => (
        <div
          title={row.task_refinement}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2',
            maxHeight: '2.4em'
          }}
        >
          {row.task_refinement}
        </div>
      )
    },
    // { name: 'Note Text', selector: row => row.note_text, sortable: true, width: '150px', wrap: true, id: 'note_text' },
    {
      name: 'Note Text',
      selector: row => row.note_text,
      sortable: true,
      width: '200px',
      id: 'note_text',
      cell: row => (
        <div
          title={row.note_text}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2',
            maxHeight: '2.4em'
          }}
        >
          {row.note_text}
        </div>
      )
    },
    // { name: 'Completion Note Text', selector: row => row.completion_note_text, sortable: true, width: '150px', wrap: true, id: 'completion_note_text' },
    {
      name: 'Completion Note Text',
      selector: row => row.completion_note_text,
      sortable: true,
      width: '200px',
      id: 'completion_note_text',
      cell: row => (
        <div
          title={row.completion_note_text}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2',
            maxHeight: '2.4em'
          }}
        >
          {row.completion_note_text}
        </div>
      )
    },
    { name: 'Status ID', selector: row => row.status_id, sortable: true, width: '150px', wrap: true, id: 'status_id' },
    { name: 'Assignee Screen Name', selector: row => row.assignee_screen_name, sortable: true, width: '150px', wrap: true, id: 'assignee_screen_name' },
    { name: 'Failure Code', selector: row => row.failure_code, sortable: true, width: '150px', wrap: true, id: 'failure_code' },
    { name: 'Created By Name', selector: row => row.created_by_name, sortable: true, width: '150px', wrap: true, id: 'created_by_name' },



    // { name: 'X Number', selector: row => row.x_number, sortable: true, width: '100px', wrap: true, id: 'x_number' },
    { name: 'Type Category', selector: row => row.type_category, sortable: true, width: '80px', wrap: true, id: 'type_category' },
    { name: 'Work Order Cost ID', selector: row => row.work_order_cost_id, sortable: true, width: '150px', wrap: true, id: 'work_order_cost_id' },
    { name: 'Priority ID', selector: row => row.priority_id, sortable: true, width: '100px', wrap: true, id: 'priority_id' },
    { name: 'Priority', selector: row => row.priority, sortable: true, width: '100px', wrap: true, id: 'priority' },
    { name: 'Requestor Contact ID', selector: row => row.requestor_contact_id, sortable: true, width: '80px', wrap: true, id: 'requestor_contact_id' },
    { name: 'Access', selector: row => row.access, sortable: true, width: '130px', wrap: true, id: 'access' },
    { name: 'Short Location', selector: row => row.short_location, sortable: true, width: '150px', wrap: true, id: 'short_location' },
    { name: 'Work Zone ID', selector: row => row.work_zone_id, sortable: true, width: '100px', wrap: true, id: 'work_zone_id' },
    { name: 'Employee ID', selector: row => row.employee_id, sortable: true, width: '100px', wrap: true, id: 'employee_id' },
    { name: 'Duration', selector: row => row.duration, sortable: true, width: '100px', wrap: true, id: 'duration' },
    // { name: 'Address ID', selector: row => row.address_id, sortable: true, width: '150px', wrap: true, id: 'address_id' },
    // { name: 'Bill To Type', selector: row => row.bill_to_type, sortable: true, width: '150px', wrap: true, id: 'bill_to_type' },
    // { name: 'Bill To Option ID', selector: row => row.bill_to_option_id, sortable: true, width: '150px', wrap: true, id: 'bill_to_option_id' },
    // { name: 'Vendor NTE Currency', selector: row => row.vendor_nte_currency, sortable: true, width: '150px', wrap: true, id: 'vendor_nte_currency' },
    // { name: 'Vendor NTE Value', selector: row => row.vendor_nte_value, sortable: true, width: '150px', wrap: true, id: 'vendor_nte_value' },
    { name: 'Specialty ID', selector: row => row.specialty_id, sortable: true, width: '100px', wrap: true, id: 'specialty_id' },
    // { name: 'PO Number', selector: row => row.po_number, sortable: true, width: '150px', wrap: true, id: 'po_number' },
    { name: 'Sub Type ID', selector: row => row.sub_type_id, sortable: true, width: '100px', wrap: true, id: 'sub_type_id' },
    { name: 'Repair Code ID', selector: row => row.repair_code_id, sortable: true, width: '100px', wrap: true, id: 'repair_code_id' },
    { name: 'Customer ID', selector: row => row.customer_id, sortable: true, width: '150px', wrap: true, id: 'customer_id' },
    // { name: 'Customer NTE Currency', selector: row => row.customer_nte_currency, sortable: true, width: '150px', wrap: true, id: 'customer_nte_currency' },
    // { name: 'Customer NTE Value', selector: row => row.customer_nte_value, sortable: true, width: '150px', wrap: true, id: 'customer_nte_value' },
    // { name: 'Billing Rule', selector: row => row.billing_rule, sortable: true, width: '150px', wrap: true, id: 'billing_rule' },
    { name: 'Costs Total Value', selector: row => row.costs_total_value, sortable: true, width: '100px', wrap: true, id: 'costs_total_value' },
    { name: 'Costs Total Currency', selector: row => row.costs_total_currency, sortable: true, width: '100px', wrap: true, id: 'costs_total_currency' },
    { name: 'Vendor Invoice Total Value', selector: row => row.vendor_invoice_total_value, sortable: true, width: '100px', wrap: true, id: 'vendor_invoice_total_value' },
    { name: 'Vendor Invoice Total Currency', selector: row => row.vendor_invoice_total_currency, sortable: true, width: '100px', wrap: true, id: 'vendor_invoice_total_currency' },
    { name: 'Expenses Total Value', selector: row => row.expenses_total_value, sortable: true, width: '100px', wrap: true, id: 'expenses_total_value' },
    { name: 'Expenses Total Currency', selector: row => row.expenses_total_currency, sortable: true, width: '100px', wrap: true, id: 'expenses_total_currency' },
    { name: 'Service Fees Total Value', selector: row => row.service_fees_total_value, sortable: true, width: '100px', wrap: true, id: 'service_fees_total_value' },
    { name: 'Service Fees Total Currency', selector: row => row.service_fees_total_currency, sortable: true, width: '100px', wrap: true, id: 'service_fees_total_currency' },
    // { name: 'Contact Name', selector: row => row.contact_name, sortable: true, width: '150px', wrap: true, id: 'contact_name' },
    // { name: 'Contact Address ID', selector: row => row.contact_address_id, sortable: true, width: '150px', wrap: true, id: 'contact_address_id' },
    // { name: 'WON ID', selector: row => row.won_id, sortable: true, width: '150px', wrap: true, id: 'won_id' },
    // { name: 'DT Scheduled Start', selector: row => row.dt_scheduled_start, sortable: true, width: '150px', wrap: true, id: 'dt_scheduled_start' },
    // { name: 'Scheduled Start UTC', selector: row => row.scheduled_start_utc, sortable: true, width: '150px', wrap: true, id: 'scheduled_start_utc' },
    { name: 'Is Warranty', selector: row => row.is_warranty ? 'Yes' : 'No', sortable: true, width: '100px', wrap: true, id: 'is_warranty' },
    // { name: 'Flag ID', selector: row => row.flag_id, sortable: true, width: '100px', wrap: true, id: 'flag_id' },
    // { name: 'Time Zone', selector: row => row.time_zone, sortable: true, width: '100px', wrap: true, id: 'time_zone' },
    // { name: 'Currency Type ID', selector: row => row.currency_type_id, sortable: true, width: '150px', wrap: true, id: 'currency_type_id' },
    // { name: 'Bill Status', selector: row => row.bill_status, sortable: true, width: '150px', wrap: true, id: 'bill_status' },
    // { name: 'AP State ID', selector: row => row.ap_state_id, sortable: true, width: '150px', wrap: true, id: 'ap_state_id' },
    // { name: 'AP Status ID', selector: row => row.ap_status_id, sortable: true, width: '150px', wrap: true, id: 'ap_status_id' },
    { name: 'Is Pre Billed', selector: row => row.is_pre_billed ? 'Yes' : 'No', sortable: true, width: '100px', wrap: true, id: 'is_pre_billed' },
    // { name: 'Cost State', selector: row => row.cost_state, sortable: true, width: '150px', wrap: true, id: 'cost_state' },
    // { name: 'Cost Status ID', selector: row => row.cost_status_id, sortable: true, width: '150px', wrap: true, id: 'cost_status_id' },
    // { name: 'Job Code', selector: row => row.job_code, sortable: true, width: '150px', wrap: true, id: 'job_code' },
    // { name: 'Authorization Code', selector: row => row.authorization_code, sortable: true, width: '150px', wrap: true, id: 'authorization_code' },
    // { name: 'Check Number', selector: row => row.check_number, sortable: true, width: '150px', wrap: true, id: 'check_number' },
    // { name: 'Payment Amount Value', selector: row => row.payment_amount_value, sortable: true, width: '150px', wrap: true, id: 'payment_amount_value' },
    // { name: 'Payment Amount Currency', selector: row => row.payment_amount_currency, sortable: true, width: '150px', wrap: true, id: 'payment_amount_currency' },
    // { name: 'Payment Notes', selector: row => row.payment_notes, sortable: true, width: '150px', wrap: true, id: 'payment_notes' },
    // { name: 'Check Cut Date', selector: row => row.check_cut_date, sortable: true, width: '150px', wrap: true, id: 'check_cut_date' },
    // { name: 'Check Clear Date', selector: row => row.check_clear_date, sortable: true, width: '150px', wrap: true, id: 'check_clear_date' },
    // { name: 'Vendor Invoice Date', selector: row => row.vendor_invoice_date, sortable: true, width: '150px', wrap: true, id: 'vendor_invoice_date' },
    // { name: 'Vendor Invoice Received Date', selector: row => row.vendor_invoice_received_date, sortable: true, width: '150px', wrap: true, id: 'vendor_invoice_received_date' },
    // { name: 'WO Items Sort Order IDX', selector: row => row.wo_items_sort_order_idx, sortable: true, width: '150px', wrap: true, id: 'wo_items_sort_order_idx' },
    // { name: 'WO Items Asset Location', selector: row => row.wo_items_asset_location, sortable: true, width: '150px', wrap: true, id: 'wo_items_asset_location' },
    // { name: 'WO Items Asset ID', selector: row => row.wo_items_asset_id, sortable: true, width: '150px', wrap: true, id: 'wo_items_asset_id' },
    // { name: 'WO Items Task ID', selector: row => row.wo_items_task_id, sortable: true, width: '150px', wrap: true, id: 'wo_items_task_id' },
    // { name: 'WO Items Comment', selector: row => row.wo_items_comment, sortable: true, width: '150px', wrap: true, id: 'wo_items_comment' },
    // { name: 'Equipment Asset ID', selector: row => row.equipment_asset_id, sortable: true, width: '150px', wrap: true, id: 'equipment_asset_id' },
    // { name: 'Equipment Comment', selector: row => row.equipment_comment, sortable: true, width: '150px', wrap: true, id: 'equipment_comment' },
    // { name: 'Equipment Refrigerant Used', selector: row => row.equipment_refrigerant_used, sortable: true, width: '150px', wrap: true, id: 'equipment_refrigerant_used' },
    // { name: 'Equipment Type', selector: row => row.equipment_type, sortable: true, width: '150px', wrap: true, id: 'equipment_type' },
    // { name: 'Procedure Template ID', selector: row => row.procedure_template_id, sortable: true, width: '150px', wrap: true, id: 'procedure_template_id' },
    // { name: 'Procedure Asset ID', selector: row => row.procedure_asset_id, sortable: true, width: '150px', wrap: true, id: 'procedure_asset_id' },
    // { name: 'Procedure Type', selector: row => row.procedure_type, sortable: true, width: '150px', wrap: true, id: 'procedure_type' },
    // { name: 'Procedure Allow Mark All', selector: row => row.procedure_allow_mark_all, sortable: true, width: '150px', wrap: true, id: 'procedure_allow_mark_all' },
    // { name: 'Procedure Allow Not Applicable', selector: row => row.procedure_allow_not_applicable, sortable: true, width: '150px', wrap: true, id: 'procedure_allow_not_applicable' },
    // { name: 'Procedure Is Not Applicable', selector: row => row.procedure_is_not_applicable, sortable: true, width: '150px', wrap: true, id: 'procedure_is_not_applicable' },
    // { name: 'Procedure Flag ID', selector: row => row.procedure_flag_id, sortable: true, width: '150px', wrap: true, id: 'procedure_flag_id' },
    // { name: 'Procedure Status ID', selector: row => row.procedure_status_id, sortable: true, width: '150px', wrap: true, id: 'procedure_status_id' },
    // { name: 'Procedure Index', selector: row => row.procedure_index, sortable: true, width: '150px', wrap: true, id: 'procedure_index' },
    // { name: 'Financial Items', selector: row => row.financial_items, sortable: true, width: '150px', wrap: true, id: 'financial_items' },
    // { name: 'Quote Items', selector: row => row.quote_items, sortable: true, width: '150px', wrap: true, id: 'quote_items' },
    // { name: 'Estimate Items', selector: row => row.estimate_items, sortable: true, width: '150px', wrap: true, id: 'estimate_items' },
    { name: 'Action Logs', selector: row => row.action_logs, sortable: true, width: '150px', wrap: true, id: 'action_logs' },
    { name: 'Last Action Date', selector: row => row.last_action_date, sortable: true, width: '150px', wrap: true, id: 'last_action_date' },
    { 
      name: 'DT Due', 
      selector: row => row.dt_due, 
      sortable: true, 
      width: '150px', 
      wrap: true,
      sortFunction: (rowA, rowB) => sortDate(rowA, rowB, 'dt_due'),
      id: 'dt_due'
    },
    { name: 'DT Acknowledge By', selector: row => row.dt_acknowledge_by, sortable: true, width: '150px', wrap: true, id: 'dt_acknowledge_by' },
    { name: 'Acknowledge By UTC', selector: row => row.acknowledge_by_utc, sortable: true, width: '150px', wrap: true, id: 'acknowledge_by_utc' },
    { name: 'Last Action Date UTC', selector: row => row.last_action_date_utc, sortable: true, width: '150px', wrap: true, id: 'last_action_date_utc' },
    { name: 'Created Date UTC', selector: row => row.created_date_utc, sortable: true, width: '150px', wrap: true, id: 'created_date_utc' },
    { name: 'Due Date UTC', selector: row => row.due_date_utc, sortable: true, width: '150px', wrap: true, id: 'due_date_utc' },
    { name: 'DT On Site By', selector: row => row.dt_on_site_by, sortable: true, width: '150px', wrap: true, id: 'dt_on_site_by' },
    { name: 'DT UTC On Site By', selector: row => row.dt_utc_on_site_by, sortable: true, width: '150px', wrap: true, id: 'dt_utc_on_site_by' },
    { name: 'Last Action ID', selector: row => row.last_action_id, sortable: true, width: '150px', wrap: true, id: 'last_action_id' },
    { name: 'Induction and Safety Status ID', selector: row => row.induction_and_safety_status_id, sortable: true, width: '150px', wrap: true, id: 'induction_and_safety_status_id' },
    { name: 'Concurrency ID', selector: row => row.concurrency_id, sortable: true, width: '150px', wrap: true, id: 'concurrency_id' },
    // { name: 'Owner ID', selector: row => row.owner_id, sortable: true, width: '150px', wrap: true, id: 'owner_id' },
    // { name: 'Evaluation IDs While Open', selector: row => row.evaluation_ids_while_open, sortable: true, width: '150px', wrap: true, id: 'evaluation_ids_while_open' },
    // { name: 'Latest Evaluation ID After Closed', selector: row => row.latest_evaluation_id_after_closed, sortable: true, width: '150px', wrap: true, id: 'latest_evaluation_id_after_closed' },
    // { name: 'Source Company ID', selector: row => row.source_company_id, sortable: true, width: '150px', wrap: true, id: 'source_company_id' },
    
    { name: 'Created At', selector: row => row.created_at, sortable: true, width: '150px', wrap: true, id: 'created_at' },
    { 
      name: 'Updated At', 
      selector: row => row.updated_at, 
      sortable: true, 
      width: '150px', 
      wrap: true,
      sortFunction: (rowA, rowB) => sortDate(rowA, rowB, 'updated_at'),
      id: 'updated_at'
    },
    { name: 'Subtype Name', selector: row => row.subtype_name, sortable: true, width: '150px', wrap: true, id: 'subtype_name' },
    { name: 'Failure Category', selector: row => row.failure_category, sortable: true, width: '150px', wrap: true, id: 'failure_category' },
    { name: 'Total Spot Cost', selector: row => row.total_spot_cost, sortable: true, width: '150px', wrap: true, id: 'total_spot_cost' },
    { 
      name: 'Is DST', 
      selector: row => row.is_dst ? 'Yes' : 'No', 
      sortable: true, 
      width: '100px', 
      wrap: true, 
      id: 'is_dst' 
    },
    // Add the avoid button column before the existing columns
    {
      name: 'Avoid',
      width: '80px',
      id: 'avoid_action',
      cell: row => {
        const isUpdating = updatingAvoid.has(row.work_order_id);
        const avoidValue = row.avoid;
        
        return (
          <button
            onClick={() => handleAvoidClick(row.work_order_id, avoidValue)}
            disabled={isUpdating}
            style={{
              backgroundColor: avoidValue === 1 ? '#dc3545' : '#28a745', // Red if avoid=1, Green if avoid=0
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '12px',
              cursor: isUpdating ? 'not-allowed' : 'pointer',
              opacity: isUpdating ? 0.6 : 1,
              minWidth: '60px'
            }}
            title={avoidValue === 1 ? 'Click to reset avoid to 0' : 'Avoid is already 0'}
          >
            {isUpdating ? '...' : (avoidValue === 1 ? 'Reset' : 'OK')}
          </button>
        );
      }
    },
  ].map(column => ({
    ...column,
    wrap: true
  }));

  const handleExportCSV = () => {
    setExportLoading(true);
    
    // Get headers from allColumns
    const headers = allColumns.map(column => column.name);
    
    // Create CSV rows using the same data and formatting as displayed in the table
    const rows = corrigoWorkOrders.map(row => {
      return allColumns.map(column => {
        // Handle different data formats based on column
        if (column.format && typeof column.format === 'function') {
          // Use the format function to get formatted value
          return `"${column.format(row).replace(/"/g, '""')}"`;
        } else if (column.selector && typeof column.selector === 'function') {
          // Use selector function to get value
          const value = column.selector(row);
          return value !== undefined && value !== null ? `"${String(value).replace(/"/g, '""')}"` : '""';
        }
        // Default to empty string if no value
        return '""';
      }).join(',');
    });
    
    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `work-orders-${moment().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => setExportLoading(false), 1000);
  };

  if (loading) {
    return <ImSpinner3 className="icon-spin" style={{ fontSize: 50, margin: 'auto', display: 'block' }} />;
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'left', color: 'red' }}>
        <h2 style={{ fontWeight: 'bold', marginBottom: '20px' }}>All Work Orders</h2>
        <p style={{ fontSize: '18px' }}>Currently facing issues gathering work orders</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
      
      <div className={`d-flex flex-row flex-no-wrap justify-start align-items-center`}>
          <Button variant="link" onClick={() => {
          goBack();
          }}>
            <MdArrowBack />
          </Button>
          <div className="font-weight-bold">{'Go Back'}</div>
        </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ fontWeight: 'bold', textAlign: 'center', margin: 0 }}>
          All Work Orders
        </h2>
        
        <div className="d-flex align-items-center">
          <Button
            variant="outline-success"
            onClick={handleExportCSV}
            disabled={loading || exportLoading || corrigoWorkOrders.length === 0}
          >
            <FaFileExport className="mr-1" /> {exportLoading ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* Favorited Properties Information */}
      {viewMode === 'favorited' && (
        <div className="d-flex justify-content-center mb-2">
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#e7f3ff',
            border: '1px solid #b3d9ff',
            borderRadius: '4px',
            color: '#004085',
            fontSize: '14px'
          }}>
            {corrigoWorkOrders.length > 0 
              ? `Showing ${corrigoWorkOrders.length} work orders for favorited properties`
              : 'No work orders found for favorited properties. You can favorite properties by clicking the star icon on any property card.'
            }
          </div>
        </div>
      )}
      
      {/* Property Group Information */}
      {viewMode === 'propertyList' && selectedPropertyList && (
        <div className="d-flex justify-content-center mb-2">
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#f0f9f1',
            border: '1px solid #c8e6c9',
            borderRadius: '4px',
            color: '#2e7d32',
            fontSize: '14px'
          }}>
            {corrigoWorkOrders.length > 0 
              ? `Showing ${corrigoWorkOrders.length} work orders for property list "${selectedPropertyList.name}"`
              : `No work orders found for property list "${selectedPropertyList.name}"`
            }
          </div>
        </div>
      )}

      {/* Show empty state for favorited view when no results */}
      {viewMode === 'favorited' && corrigoWorkOrders.length === 0 && !loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          color: '#6c757d',
          fontSize: '16px'
        }}>
          <div style={{ marginBottom: '10px' }}>
            🌟 No work orders found for your favorited properties!
          </div>
          <div style={{ fontSize: '14px' }}>
            {hasFavorites 
              ? 'Try adjusting the date range or switch back to "All Work Orders" to see more results.'
              : 'You can favorite properties by clicking the star icon on any property card in the Inspection page.'
            }
          </div>
        </div>
      )}
      
      {/* Show empty state for property list view when no results */}
      {viewMode === 'propertyList' && corrigoWorkOrders.length === 0 && !loading && selectedPropertyList && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          color: '#6c757d',
          fontSize: '16px'
        }}>
          <div style={{ marginBottom: '10px' }}>
            📋 No work orders found for property list "{selectedPropertyList.name}"!
          </div>
          <div style={{ fontSize: '14px' }}>
            Try adjusting the date range or switch to "All Work Orders" to see more results.
          </div>
        </div>
      )}

      <DataTable
        columns={allColumns}
        data={corrigoWorkOrders}
        noDataComponent={
          viewMode === 'favorited' ? 
            "No work orders found for favorited properties." : 
            viewMode === 'propertyList' ?
            `No work orders found for property list "${selectedPropertyList?.name || 'selected'}".` :
            "No Corrigo Work Orders found."
        }
        responsive
        highlightOnHover
        persistTableHead
        defaultSortFieldId="dt_created"
        defaultSortAsc={false}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[100, 250, 500, 1000]}
        paginationComponentOptions={{
          rowsPerPageText: 'Rows per page:',
          rangeSeparatorText: 'of',
          noRowsPerPage: false,
          selectAllRowsItem: false,
        }}
        style={{ border: '1px solid #ddd', borderRadius: '8px' }}
        customStyles={{
          headCells: {
            style: {
              backgroundColor: '#e3e3e3',
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#333',
            },
          },
          cells: {
            style: {
              padding: '6px 8px',
              fontSize: '12px',
              color: '#333',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxHeight: '40px',
              lineHeight: '1.4',
            },
          },
        }}
      />

{hasMore && !loading && (
        <Button variant="primary" onClick={handleLoadMore}>
          Load More
        </Button>
      )}

      {loading && <ImSpinner3 className="icon-spin" style={{ fontSize: 50, margin: 'auto', display: 'block' }} />}
      
      {error && (
        <div style={{ padding: '20px', textAlign: 'left', color: 'red' }}>
          <h2 style={{ fontWeight: 'bold', marginBottom: '20px' }}>All Work Orders</h2>
          <p style={{ fontSize: '18px' }}>Currently facing issues gathering work orders</p>
        </div>
      )}
    </div>
  );
};

export default WorkOrdersScreen;
